import React from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../reducers';
import { getOrderStringToInt } from '../../../../utils/Utils';
import { AsyncDispatch } from '../../../../../types/global';
import { useAppDispatch } from '../../../../store/ReduxStore';
import AccountFiltersTable from './AccountFiltersTable';
import { fetchAccountFilters } from '../../../../actions/parent/accountConfigurations/fetch';
import { fetchAllAccountFilters } from '../../../../actions/catalogue/filter/fetch';
import { fetchUserNames } from '../../../../actions/user';
import { activeBrands } from '../../../../selectors/brand/brandSelector';
import AntMultiSelect from '../../../global/AntMultiSelect';

const AccountFiltersPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();

  const [selectedCustomFieldIds, setSelectedCustomFieldIds] = React.useState<number[] | undefined>(
    undefined
  );
  const [selectedNames, setSelectedNames] = React.useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = React.useState<number[]>([]);
  const [selectedGlobalStatusIds, setSelectedGlobalStatusIds] = React.useState<number[]>([]);
  const [selectedBrandIds, setSelectedBrandIds] = React.useState<number[]>([]);
  const [sortColumn, setSortColumn] = React.useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = React.useState<string | undefined>(undefined);

  const brands = useSelector((state: ApplicationState) => activeBrands(state));
  const { filters, isLoading, hasMoreData, customFields, accountFilters, userNameList } =
    useSelector((state: ApplicationState) => {
      return {
        filters: state.parent.configurations.filters.data,
        isLoading: state.parent.configurations.filters.isLoading,
        hasMoreData: state.parent.configurations.filters.hasMoreData,
        customFields: state.parent.parentCustomFields.customFields,
        accountFilters: state.catalogue.filter.accountFilters,
        userNameList: state.user.userNameList,
      };
    }, shallowEqual);

  const lastFetchedPageRef = React.useRef(0);

  const filterKey = React.useMemo(() => {
    const parts = [
      selectedNames.join(','),
      selectedUserIds.join(','),
      selectedGlobalStatusIds.join(','),
      selectedBrandIds.join(','),
      selectedCustomFieldIds?.join(',') || '',
      sortColumn || '',
      sortOrder || '',
    ];
    return parts.join('_');
  }, [
    selectedNames,
    selectedUserIds,
    selectedGlobalStatusIds,
    selectedBrandIds,
    selectedCustomFieldIds,
    sortColumn,
    sortOrder,
  ]);

  React.useEffect(() => {
    if (accountFilters.length === 0) {
      dispatch(fetchAllAccountFilters());
    }
  }, [accountFilters.length, dispatch]);

  React.useEffect(() => {
    if (userNameList.length === 0) {
      dispatch(fetchUserNames());
    }
  }, [userNameList.length, dispatch]);

  React.useEffect(() => {
    lastFetchedPageRef.current = 0;
  }, [filterKey]);

  const fetchFilteredFilters = React.useCallback(
    (
      customFieldIds?: number[],
      names?: string[],
      userIds?: number[],
      globalStatuses?: number[],
      brandIds?: number[],
      sortCol?: string,
      sortOrd?: string
    ) => {
      lastFetchedPageRef.current = 1;
      dispatch(
        fetchAccountFilters({
          page: 1,
          filterNames: names || undefined,
          filterUserId: userIds || undefined,
          filterGlobalStatuses: globalStatuses || undefined,
          filterBrandIds: brandIds || undefined,
          customFieldIds: customFieldIds || undefined,
          orderCondition: sortCol,
          descending: getOrderStringToInt(sortOrd),
        })
      );
    },
    [dispatch]
  );

  const handleCustomFieldsFilter = React.useCallback(
    (value: number[]) => {
      const ids = value.length > 0 ? value : undefined;
      setSelectedCustomFieldIds(ids);
      fetchFilteredFilters(
        ids,
        selectedNames,
        selectedUserIds,
        selectedGlobalStatusIds,
        selectedBrandIds,
        sortColumn,
        sortOrder
      );
    },
    [
      fetchFilteredFilters,
      selectedNames,
      selectedUserIds,
      selectedGlobalStatusIds,
      selectedBrandIds,
      sortColumn,
      sortOrder,
    ]
  );

  const fetchNextAccountFilters = (page: number) => {
    return dispatch(
      fetchAccountFilters({
        page,
        filterNames: selectedNames || undefined,
        filterUserId: selectedUserIds || undefined,
        filterGlobalStatuses: selectedGlobalStatusIds || undefined,
        filterBrandIds: selectedBrandIds || undefined,
        customFieldIds: selectedCustomFieldIds || undefined,
        orderCondition: sortColumn,
        descending: getOrderStringToInt(sortOrder),
      })
    );
  };

  const handleSort = (column: string, order: string) => {
    const orderDirection = sortColumn === column && sortOrder === order ? '' : order;
    const newColumn = orderDirection ? column : undefined;

    setSortColumn(newColumn);
    setSortOrder(orderDirection);

    fetchFilteredFilters(
      selectedCustomFieldIds,
      selectedNames,
      selectedUserIds,
      selectedGlobalStatusIds,
      selectedBrandIds,
      newColumn,
      orderDirection
    );
  };

  const handleFilterByColumns = React.useCallback(
    (names: string[], userIds: number[], globalStatuses: number[], brandIds: number[]) => {
      setSelectedNames(names);
      setSelectedUserIds(userIds);
      setSelectedGlobalStatusIds(globalStatuses);
      setSelectedBrandIds(brandIds);
      fetchFilteredFilters(
        selectedCustomFieldIds,
        names,
        userIds,
        globalStatuses,
        brandIds,
        sortColumn,
        sortOrder
      );
    },
    [fetchFilteredFilters, selectedCustomFieldIds, sortColumn, sortOrder]
  );

  return (
    <div className="page-layout">
      <div className="page-layout__top-bar">
        <div className="page-layout__top-bar__container">
          <AntMultiSelect
            className="max-w-md"
            selectedElements={selectedCustomFieldIds || []}
            onChange={handleCustomFieldsFilter}
            placeholder={t('configurations:filterByCustomFields')}
            allowClear
            style={{ minWidth: '300px' }}
            elements={customFields.map(field => ({
              id: field.id,
              name: field.name,
            }))}
            showSearch
          />
        </div>
      </div>
      <div className="page-layout__content flex">
        {filters.length === 0 && isLoading ? (
          <div className="loader" />
        ) : (
          <AccountFiltersTable
            key={filterKey}
            fetchingAccountFilters={isLoading}
            filters={filters}
            brands={brands}
            users={userNameList}
            hasMoreData={hasMoreData}
            fetchNextAccountFilters={fetchNextAccountFilters}
            selectedNames={selectedNames}
            selectedUserIds={selectedUserIds}
            selectedGlobalStatusIds={selectedGlobalStatusIds}
            selectedBrandIds={selectedBrandIds}
            allFilterNames={accountFilters}
            handleFilterByColumns={handleFilterByColumns}
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSort={handleSort}
            lastFetchedPageRef={lastFetchedPageRef}
          />
        )}
      </div>
    </div>
  );
};

export default AccountFiltersPage;
